import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { cn } from '@/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import React, { useEffect, useRef, useState } from 'react';

interface SelectDropDownProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  initialValue?: number;
  valueChangeCallback: CallableFunction;
  list: Item[] | undefined;
  placeholder?: string;
  className?: string;
}

interface Item {
  id: number;
  name: string;
}

const SelectDropDown = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Root>, SelectDropDownProps>(
  ({ initialValue, valueChangeCallback, list, placeholder, className, ...props }, ref) => {
    const matchById = (id: number): Item | undefined => list?.filter((item) => item.id === id)?.[0];

    const self = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<Item | undefined>(
      initialValue ? matchById(initialValue) : undefined,
    );
    const [width, setWidth] = useState<number>(200);

    useEffect(() => {
      if (initialValue != selectedItem?.id) {
        setSelectedItem(matchById(initialValue ?? 0));
        setWidth(self.current?.offsetWidth ?? 200);
      }
    }, [initialValue]);

    return (
      <Select
        disabled={list ? list?.length == 0 : true}
        onValueChange={(id) => {
          const idValue = Number(id);
          valueChangeCallback(idValue);
          setSelectedItem(matchById(idValue));
        }}
        defaultValue={selectedItem ? selectedItem.name : placeholder}
        open={open}
        onOpenChange={setOpen}
        required={true}
        {...props}
      >
        <SelectTrigger
          ref={self}
          isOpen={open}
          className={cn(
            `flex h-10 rounded-lg bg-white text-b3 text-secondary-grey-01 transition-colors duration-150 ease-in-out
            focus-visible:outline-none aria-[invalid]:border-tertiary-red aria-[invalid]:bg-red-200 aria-[invalid]:text-red-800`,
            className,
          )}
          tabIndex={-1}
          title={selectedItem ? selectedItem.name : undefined}
        >
          <SelectValue
            className={`flex h-10 w-full flex-shrink flex-grow justify-between self-stretch border-[1px] border-input bg-white px-5 py-3
              font-normal leading-6 text-secondary-grey-01 hover:bg-white ${open ? 'ring-1 ring-deep-blue' : ''}`}
          >
            <span className="w-full text-start text-b2">{selectedItem ? selectedItem.name : placeholder}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {list?.map((item) => (
            <SelectItem key={item.id} className={'w-full py-[10px] text-b2'} value={item.id.toString()}>
              <span className="text-b2">{item.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  },
);
SelectDropDown.displayName = 'SelectDropDown';

export { SelectDropDown };
